// src/components/HRComponents/PolicyAssign.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import SelectDropdown from '../../customStyles/SelectDropdown';

// FIX: Changed 'Specific Employee Group(s)' to 'Specific Division(s)' and mapped value to 'division'
const ASSIGNMENT_TYPES = [
  { label: 'All Employees', value: 'all_employees' },
  { label: 'Specific Department(s)', value: 'department' },
  { label: 'Specific Designation(s)', value: 'designation' },
  { label: 'Specific Division(s)', value: 'division' }, 
  { label: 'Individual Employees', value: 'individual' },
];

const PolicyAssign = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assignmentType, setAssignmentType] = useState('all_employees');
  const [targetIds, setTargetIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch lists dynamically based on chosen assignment type
  const fetchOptionsForType = useCallback(async (type) => {
    if (type === 'all_employees') return setOptions([]);
    
    // FIX: Replaced 'employee_group' with 'division' and mapped to SummaryApi.getAllDivisions
    const urlMap = {
      department: SummaryApi.getAllDepartments?.url,
      designation: SummaryApi.getAllDesignations?.url,
      division: SummaryApi.getAllDivisions?.url, // Maps to division endpoints on your backend
      individual: SummaryApi.getAllUsers?.url,
    };
    
    const url = urlMap[type];
    if (!url) return setOptions([]);
    try {
      const res = await api.get(url);
      if (res.data.success) setOptions(res.data.data || []);
    } catch {
      setOptions([]);
    }
  }, []);

  const fetchExisting = useCallback(async () => {
    try {
      const res = await api.get(SummaryApi.getPolicyAssignments.url(id));
      if (res.data.success) setExistingAssignments(res.data.assignments || []);
    } catch {
      // non-fatal
    }
  }, [id]);

  useEffect(() => { fetchOptionsForType(assignmentType); }, [assignmentType, fetchOptionsForType]);
  useEffect(() => { fetchExisting(); }, [fetchExisting]);

  const handleSubmit = async () => {
    if (assignmentType !== 'all_employees' && targetIds.length === 0) {
      return toast.error('Select at least one target');
    }
    setIsSaving(true);
    try {
      await api.post(SummaryApi.assignPolicy.url(id), { assignmentType, targetIds });
      toast.success('Assignment saved');
      fetchExisting();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save assignment');
    } finally {
      setIsSaving(false);
    }
  };

  // FIX: Checked 'title' as a fallback to support Designation models elegantly [1]
  const getOptionLabel = (o) => o.name || o.title || `${o.firstName || ''} ${o.lastName || ''}`.trim();

  return (
    <div className="p-4">
      {/* Header section with back button */}
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 p-1.5 sm:p-2">
          <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Manage Assignment</h1>
      </div>

      <div className="bg-white border rounded-xl p-6 space-y-5">
        <SelectDropdown
          label="Assign To"
          options={ASSIGNMENT_TYPES}
          value={assignmentType}
          onChange={(v) => { setAssignmentType(v); setTargetIds([]); }}
          valueKey="value" labelKey="label"
        />

        {assignmentType !== 'all_employees' && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Select Targets</label>
            <div className="border rounded-xl max-h-64 overflow-y-auto divide-y">
              {options.map((o) => (
                <label key={o._id} className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={targetIds.includes(o._id)}
                    onChange={(e) => {
                      setTargetIds((prev) => e.target.checked ? [...prev, o._id] : prev.filter((x) => x !== o._id));
                    }}
                  />
                  {getOptionLabel(o)}
                </label>
              ))}
              {options.length === 0 && <p className="text-sm text-gray-400 px-4 py-3">No options available</p>}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={handleSubmit} disabled={isSaving} className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50">
            {isSaving ? 'Saving...' : 'Save Assignment'}
          </button>
        </div>
      </div>

      {existingAssignments.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Current Assignment(s)</h2>
          <div className="space-y-2">
            {existingAssignments.map((a) => (
              <div key={a._id} className="bg-white border rounded-lg p-3 text-sm flex justify-between">
                <span className="capitalize">{a.assignmentType.replace('_', ' ')}</span>
                <span className="text-gray-500">{a.resolvedCount} employees</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyAssign;